import React from "react";
import { Button } from "../button";
import { EmptyPanel } from "../panel";
import { EmailMessage } from "./EmailMessage";

export type EmailTemplatePreviewProps = {
  title: string;
  recipient: string;
  subject: string;
  /** Trusted, server-rendered template HTML; user-controlled values must already be escaped. */
  html?: string;
  /** Editable message content; uses the same email typography as HTML previews. */
  body?: React.ReactNode;
  error?: string;
  onRetry?: () => void;
  children?: React.ReactNode;
};

/** An illustrative email with optional configuration immediately above its body. */
export function EmailTemplatePreview({
  title,
  recipient,
  subject,
  html,
  body,
  error,
  onRetry,
  children,
}: EmailTemplatePreviewProps) {
  return (
    <section className="tw-boundary" aria-label={title}>
      <div className="max-w-[800px]">
        <h3 className="m-0 text-sm font-normal text-neutral-900">{title}</h3>
        {children && (
          <div className="my-[16px]">
            <EmptyPanel widthClass="w-full bg-white">{children}</EmptyPanel>
          </div>
        )}
        <div className="mt-[6px] bg-neutral-0 p-[24px] sm:p-[32px]" aria-label="Email preview">
          <EmailMessage recipient={recipient} subject={subject}>
            {error ? (
              <div>
                <p role="alert">{error}</p>
                {onRetry && (
                  <Button color="tertiary" onClick={onRetry}>
                    Retry preview
                  </Button>
                )}
              </div>
            ) : (
              body ??
              (html === undefined ? (
                <p role="status">Loading preview…</p>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: html }} />
              ))
            )}
          </EmailMessage>
        </div>
      </div>
    </section>
  );
}
